import React, { useState, useEffect } from 'react';
import { X, Plus, Minus, CreditCard, Smartphone, Building2, Coins, TrendingUp } from 'lucide-react';
import { getTodayIST, dateInputToISO } from '../utils/dateUtils';

// Main Payment Type Selector Modal
export const PaymentTypeSelector = ({ isOpen, onClose, onSelectType }) => {
  if (!isOpen) return null;

  const paymentTypes = [
    { id: 'cash', label: 'Cash', icon: '💵' },
    { id: 'card', label: 'Card', icon: '💳' },
    { id: 'upi', label: 'UPI', icon: '📱' },
    { id: 'bank', label: 'Bank Transfer', icon: '🏦' },
    { id: 'old-gold', label: 'Old Gold', icon: '🪙' },
    { id: 'metal', label: 'Metal (Fine Gold)', icon: '✨' }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center z-50" onClick={onClose}>
      <div 
        className="bg-white rounded-t-3xl w-full max-w-md p-6 space-y-4 animate-slide-up max-h-[85vh] overflow-y-auto" 
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-lg font-bold text-gray-800">Add Payment/Deposit</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          {paymentTypes.map(type => (
            <button
              key={type.id}
              onClick={() => {
                onSelectType(type.id);
                onClose();
              }}
              className="flex flex-col items-center justify-center p-4 rounded-xl bg-gradient-to-br from-amber-50 to-orange-50 hover:from-amber-100 hover:to-orange-100 border-2 border-amber-200 transition-all min-h-[100px]"
            >
              <span className="text-3xl mb-2">{type.icon}</span>
              <span className="text-xs font-semibold text-gray-800 text-center">{type.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// Cash Payment Modal
export const CashPaymentModal = ({ isOpen, onClose, onSave, editData }) => {
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(getTodayIST());
  const [note, setNote] = useState('');

  // Pre-fill if editing
  useEffect(() => {
    if (editData) {
      setAmount(editData.amount?.toString() || '');
      setDate(editData.date || getTodayIST());
      setNote(editData.note || '');
    } else {
      setAmount('');
      setDate(getTodayIST());
      setNote('');
    }
  }, [editData, isOpen]);

  if (!isOpen) return null;

  const handleSave = () => {
    if (!amount || amount === '0') return;
    const amountValue = parseFloat(amount);
    onSave({
      mode: 'Cash',
      amount: Math.abs(amountValue),
      date: dateInputToISO(date),
      note,
      reference: note,
      transaction_type: amountValue < 0 ? 'REFUND' : 'PAYMENT',
      direction: amountValue < 0 ? 'OUT' : 'IN',
      timestamp: editData?.timestamp || dateInputToISO(date)
    });
    onClose();
  };

  const isRefund = parseFloat(amount) < 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center z-50" onClick={onClose}>
      <div 
        className="bg-white rounded-t-3xl w-full max-w-md p-6 space-y-4 animate-slide-up max-h-[85vh] overflow-y-auto" 
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center">
          <h3 className={`text-lg font-bold ${isRefund ? 'text-red-600' : 'text-gray-800'}`}>
            {isRefund ? '↩️ Cash Refund' : '💵 Cash Payment'}
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Amount (₹)</label>
            <input
              type="number"
              step="0.01"
              className="input-field w-full"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Enter amount (use - for refund)"
            />
            <p className="text-xs text-gray-500 mt-1">
              💡 Tip: Enter negative amount (e.g., -5000) to record a refund
            </p>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Date</label>
            <input
              type="date"
              className="input-field w-full"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Note (optional)</label>
            <input
              type="text"
              className="input-field w-full"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Payment note"
            />
          </div>
        </div>

        <div className="flex gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!amount}
            className="flex-1 py-3 bg-gradient-to-r from-amber-400 to-orange-400 text-white rounded-lg font-semibold disabled:opacity-50"
          >
            {editData ? 'Update' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Card Payment Modal
export const CardPaymentModal = ({ isOpen, onClose, onSave, editData }) => {
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(getTodayIST());
  const [note, setNote] = useState('');
  const [cardLast4, setCardLast4] = useState('');
  const [posRef, setPosRef] = useState('');

  // Pre-fill if editing
  useEffect(() => {
    if (editData) {
      setAmount(editData.amount?.toString() || '');
      setDate(editData.date || getTodayIST());
      setNote(editData.note || '');
      setCardLast4(editData.card_last4 || '');
      setPosRef(editData.pos_ref || '');
    } else {
      setAmount('');
      setDate(getTodayIST());
      setNote('');
      setCardLast4('');
      setPosRef('');
    }
  }, [editData, isOpen]);

  if (!isOpen) return null;

  const handleSave = () => {
    if (!amount || amount === '0') return;
    const amountValue = parseFloat(amount);
    onSave({
      mode: 'Card',
      amount: Math.abs(amountValue),
      date: dateInputToISO(date),
      note,
      reference: `Card ending ${cardLast4} - POS: ${posRef}`,
      card_last4: cardLast4,
      pos_ref: posRef,
      transaction_type: amountValue < 0 ? 'REFUND' : 'PAYMENT',
      direction: amountValue < 0 ? 'OUT' : 'IN',
      timestamp: editData?.timestamp || dateInputToISO(date)
    });
    onClose();
  };

  const isRefund = parseFloat(amount) < 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center z-50" onClick={onClose}>
      <div 
        className="bg-white rounded-t-3xl w-full max-w-md p-6 space-y-4 animate-slide-up max-h-[85vh] overflow-y-auto" 
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center">
          <h3 className={`text-lg font-bold ${isRefund ? 'text-red-600' : 'text-gray-800'}`}>
            {isRefund ? '↩️ Card Refund' : '💳 Card Payment'}
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Amount (₹)</label>
            <input
              type="number"
              step="0.01"
              className="input-field w-full"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Enter amount (use - for refund)"
            />
            <p className="text-xs text-gray-500 mt-1">
              💡 Tip: Enter negative amount (e.g., -5000) to record a refund
            </p>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Date</label>
            <input
              type="date"
              className="input-field w-full"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Card Last 4 Digits (optional)</label>
            <input
              type="text"
              maxLength="4"
              className="input-field w-full"
              value={cardLast4}
              onChange={(e) => setCardLast4(e.target.value.replace(/\D/g, ''))}
              placeholder="1234"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">POS Reference (optional)</label>
            <input
              type="text"
              className="input-field w-full"
              value={posRef}
              onChange={(e) => setPosRef(e.target.value)}
              placeholder="Transaction reference"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Note (optional)</label>
            <input
              type="text"
              className="input-field w-full"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Payment note"
            />
          </div>
        </div>

        <div className="flex gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!amount}
            className="flex-1 py-3 bg-gradient-to-r from-amber-400 to-orange-400 text-white rounded-lg font-semibold disabled:opacity-50"
          >
            {editData ? 'Update' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

// UPI Payment Modal
export const UPIPaymentModal = ({ isOpen, onClose, onSave, editData }) => {
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(getTodayIST());
  const [note, setNote] = useState('');
  const [upiRef, setUpiRef] = useState('');
  const [upiHandle, setUpiHandle] = useState('');

  // Pre-fill if editing
  useEffect(() => {
    if (editData) {
      setAmount(editData.amount?.toString() || '');
      setDate(editData.date || getTodayIST());
      setNote(editData.note || '');
      setUpiRef(editData.upi_ref || '');
      setUpiHandle(editData.upi_handle || '');
    } else {
      setAmount('');
      setDate(getTodayIST());
      setNote('');
      setUpiRef('');
      setUpiHandle('');
    }
  }, [editData, isOpen]);

  if (!isOpen) return null;

  const handleSave = () => {
    if (!amount || amount === '0') return;
    const amountValue = parseFloat(amount);
    onSave({
      mode: 'UPI',
      amount: Math.abs(amountValue),
      date: dateInputToISO(date),
      note,
      reference: `UPI: ${upiHandle} - Ref: ${upiRef}`,
      upi_ref: upiRef,
      upi_handle: upiHandle,
      transaction_type: amountValue < 0 ? 'REFUND' : 'PAYMENT',
      direction: amountValue < 0 ? 'OUT' : 'IN',
      timestamp: editData?.timestamp || dateInputToISO(date)
    });
    onClose();
  };

  const isRefund = parseFloat(amount) < 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center z-50" onClick={onClose}>
      <div 
        className="bg-white rounded-t-3xl w-full max-w-md p-6 space-y-4 animate-slide-up max-h-[85vh] overflow-y-auto" 
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center">
          <h3 className={`text-lg font-bold ${isRefund ? 'text-red-600' : 'text-gray-800'}`}>
            {isRefund ? '↩️ UPI Refund' : '📱 UPI Payment'}
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Amount (₹)</label>
            <input
              type="number"
              step="0.01"
              className="input-field w-full"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Enter amount (use - for refund)"
            />
            <p className="text-xs text-gray-500 mt-1">
              💡 Tip: Enter negative amount (e.g., -5000) to record a refund
            </p>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">UPI Ref/Txn ID (optional)</label>
            <input
              type="text"
              className="input-field w-full"
              value={upiRef}
              onChange={(e) => setUpiRef(e.target.value)}
              placeholder="e.g., 405612345678"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">UPI Handle (optional)</label>
            <input
              type="text"
              className="input-field w-full"
              value={upiHandle}
              onChange={(e) => setUpiHandle(e.target.value)}
              placeholder="e.g., user@paytm"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Date</label>
            <input
              type="date"
              className="input-field w-full"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Note (optional)</label>
            <input
              type="text"
              className="input-field w-full"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Payment note"
            />
          </div>
        </div>

        <div className="flex gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!amount}
            className="flex-1 py-3 bg-gradient-to-r from-amber-400 to-orange-400 text-white rounded-lg font-semibold disabled:opacity-50"
          >
            {editData ? 'Update' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};
